import {EmbedProps} from '@frontapp/editor-html/lib/services/embed/embedService';
import React, {FC, ReactElement, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  CalendarsPanelId,
  ComposerPanelId,
  makeCalendarsPanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {MeetingTimesData} from './meetingsettings/meetingSettingsPanel';
import {MeetingTimesDialogContainer} from './meetingTimesDialogContainer';

/*
 * Props.
 */

interface SchedulingLinkAvailabilityListEmbedProps extends EmbedProps {
  panelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId;
  schedulingLinkId: number;
  parentElement: Element;
  renderAvailability: (meetingTimesData: MeetingTimesData) => ReactElement;
}

/*
 * Style.
 */

const StyledEmbedContainer = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  display: block !important;

  pointer-events: none;

  /* We need to reset box-sizing since it is set to initial in contenteditables. */
  *,
  *::after,
  *::before {
    box-sizing: border-box;
  }
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: -7px;
  bottom: -15px;
  left: -8px;
  width: 286px;
  border-radius: 6px;

  background-color: ${(p) => p.theme.alphas.gray10};
`;

const StyledActionsDiv = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;

  display: grid;
  grid-gap: 4px;
  grid-auto-flow: column;
  grid-auto-columns: max-content;

  pointer-events: auto;
`;

const StyledIcon = styled(Icon)`
  height: 16px;
  width: 16px;
  border-radius: 8px;

  display: flex;
  padding: 0 2px;
  line-height: 16px;

  color: ${(p) => p.theme.greys.white};
  background-color: ${(p) => p.theme.greys.shade70};

  &:hover {
    background-color: ${(p) => p.theme.greys.shade80};
  }
`;

/*
 * Component.
 */

export const SchedulingLinkAvailabilityListEmbed: FC<SchedulingLinkAvailabilityListEmbedProps> = (props) => {
  const [isShowingEmbed, setIsShowingEmbed] = useState<boolean>(false);

  const [dialogPanelId, setDialogPanelId] = useState<CalendarsPanelId>();
  const onEmbedClick = () => setDialogPanelId(makeCalendarsPanelId());
  const onDialogClose = () => setDialogPanelId(undefined);

  useEffect(() => {
    const onMouseEnter = () => setIsShowingEmbed(true);
    const onMouseLeave = () => setIsShowingEmbed(false);

    props.parentElement.addEventListener('mouseenter', onMouseEnter);
    props.parentElement.addEventListener('mouseleave', onMouseLeave);

    return () => {
      props.parentElement.removeEventListener('mouseenter', onMouseEnter);
      props.parentElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [props.parentElement]);

  return (
    <>
      {/* The embed should be hidden without styled components. */}
      <StyledEmbedContainer style={{display: 'none'}}>
        {isShowingEmbed && renderEmbedContent(props, onEmbedClick)}
      </StyledEmbedContainer>
      {dialogPanelId && (
        <MeetingTimesDialogContainer
          onDialogClose={onDialogClose}
          onInsertMeetingTimes={(meetingTimesData) => {
            onDialogClose();
            props.onUpdateContent(props.renderAvailability(meetingTimesData));
          }}
          panelId={dialogPanelId}
          parentPanelId={props.panelId}
          schedulingLinkId={props.schedulingLinkId}
        />
      )}
    </>
  );
};

function renderEmbedContent(props: SchedulingLinkAvailabilityListEmbedProps, editSchedulingLink: () => void) {
  const editLabel = <Trans id="composer-scheduling-edit">Edit available times</Trans>;
  const removeLabel = <Trans id="composer-scheduling-remove">Delete all</Trans>;

  return (
    <StyledOverlayDiv>
      <StyledActionsDiv>
        <TooltipCoordinator render={() => editLabel}>
          <ChromelessButton onClick={editSchedulingLink}>
            <StyledIcon name="editTiny" />
            <VisuallyHidden>{editLabel}</VisuallyHidden>
          </ChromelessButton>
        </TooltipCoordinator>
        <TooltipCoordinator render={() => removeLabel}>
          <ChromelessButton onClick={() => props.onDeleteEmbed()}>
            <StyledIcon name="exitTiny" />
            <VisuallyHidden>{removeLabel}</VisuallyHidden>
          </ChromelessButton>
        </TooltipCoordinator>
      </StyledActionsDiv>
    </StyledOverlayDiv>
  );
}
